import type { BulkApproveExtranetPendingChangesResponse } from '~/services/ExtranetService';

import { isRecord } from '~/utils/isRecord';

import { DEFAULT_APPROVAL_FAILURE_MESSAGE } from './constants';
import type { ExtranetReplayFailure } from './types';
import {
  extractTechnicalReplayDetail,
  getMessageForReplayCode,
  getValidationIssuesFromDetail,
  hasReplayFailureSignals,
  inferReplayCodeFromDetail,
  mapUpstreamCodeToReplayCode,
  summarizeFacilityValidationIssues,
} from './validationIssues';

function buildReplayFailureSource(body: Record<string, unknown>) {
  const upstreamCode = typeof body.code === 'string' ? body.code : undefined;
  const replayDetail = extractTechnicalReplayDetail(body);
  const replayCode =
    (typeof body.replayCode === 'string' ? body.replayCode : undefined) ||
    mapUpstreamCodeToReplayCode(upstreamCode) ||
    inferReplayCodeFromDetail(replayDetail ?? body);

  const validationSummary = summarizeFacilityValidationIssues(getValidationIssuesFromDetail(replayDetail ?? body));
  const rawReplayError =
    (typeof body.replayError === 'string' ? body.replayError : undefined) ||
    (typeof body.message === 'string' ? body.message : undefined);

  const agentMessage =
    (typeof body.agentMessage === 'string' ? body.agentMessage : undefined) ||
    validationSummary ||
    getMessageForReplayCode(replayCode) ||
    (typeof body.replayError === 'string' ? body.replayError : undefined) ||
    (typeof body.message === 'string' ? body.message : undefined);

  return {
    agentMessage,
    replayCode,
    replayDetail,
    rawReplayError,
  };
}

export function parseExtranetReplayFailure(responseBody: unknown): ExtranetReplayFailure | null {
  if (!isRecord(responseBody) || !hasReplayFailureSignals(responseBody)) {
    return null;
  }

  const { agentMessage, replayCode, replayDetail, rawReplayError } = buildReplayFailureSource(responseBody);

  if (!agentMessage && replayDetail === undefined && !replayCode && !rawReplayError) {
    return null;
  }

  return {
    agentMessage: agentMessage || DEFAULT_APPROVAL_FAILURE_MESSAGE,
    replayCode,
    replayDetail,
    rawReplayError,
  };
}

function getBulkResultReplayStatus(
  item: BulkApproveExtranetPendingChangesResponse['results'][number],
): number | undefined {
  if (typeof item.replayStatus === 'number') {
    return item.replayStatus;
  }

  if (isRecord(item.replayDetail) && typeof item.replayDetail.status === 'number') {
    return item.replayDetail.status;
  }

  return undefined;
}

export function parseBulkApproveReplayFailures(
  result: BulkApproveExtranetPendingChangesResponse,
): Array<ExtranetReplayFailure> {
  return (result.results ?? []).flatMap((item) => {
    if (!item.replayError && item.replayDetail === undefined) {
      return [];
    }

    const replayStatus = getBulkResultReplayStatus(item);

    const source = isRecord(item.replayDetail)
      ? {
          ...item.replayDetail,
          replayError: item.replayError,
          ...(replayStatus !== undefined && { replayStatus }),
        }
      : {
          replayError: item.replayError,
          replayDetail: item.replayDetail,
          ...(replayStatus !== undefined && { replayStatus }),
        };

    const failure = parseExtranetReplayFailure(source);
    return failure ? [failure] : [];
  });
}
